import type { Integration } from "@repo/api";
import { integrationApi } from "@repo/api";
import { ApplicationIcon, SidePanel } from "@saviynt/design-system";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { DeleteIntegrationModal } from "../../DeleteIntegrationModal";
import { IntegrationSidePanelFooter } from "../../IntegrationSidePanel";
import { useIntegrationById } from "../../../hooks/useIntegrationById";
import { getApplicationLogo } from "../../../utils/integrationIcons";
import { AgentforceIntegrationSidePanelContent } from "./AgentforceIntegrationSidePanelContent";

interface AgentforceIntegrationSidePanelProps {
  integration: Integration | null;
  isOpen: boolean;
  onClose: () => void;
}

export function AgentforceIntegrationSidePanel({
  integration,
  isOpen,
  onClose,
}: AgentforceIntegrationSidePanelProps) {
  const intl = useIntl();
  const sidepanelRef = useRef(null);
  const [isTabletMediumWidth, setIsTabletMediumWidth] = useState(false);
  const [isMobileXlargeWidth, setIsMobileXlargeWidth] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data: fullIntegration } = useIntegrationById(
    isOpen && integration?.id ? Number(integration.id) : undefined,
  );
  const integrationData = fullIntegration ?? integration;

  const {
    data: credentials,
    isLoading: isLoadingCredentials,
    isError: isCredentialsError,
  } = useQuery({
    queryKey: ["integrationCredentials", integration?.id],
    queryFn: async () => {
      if (!integration?.id) {
        throw new Error("Integration ID is required");
      }
      return await integrationApi.getIntegrationCredentials(integration.id);
    },
    enabled: !!integration?.id && isOpen,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    const checkBreakpoints = () => {
      setIsTabletMediumWidth(window.matchMedia("(max-width: 833px)").matches);
      setIsMobileXlargeWidth(window.matchMedia("(max-width: 599px)").matches);
    };
    checkBreakpoints();
    window.addEventListener("resize", checkBreakpoints);
    return () => window.removeEventListener("resize", checkBreakpoints);
  }, []);

  return (
    <>
      <SidePanel
        ref={sidepanelRef}
        header={{
          title:
            integrationData?.displayName ||
            intl.formatMessage({
              id: "u3fbbP",
              defaultMessage: "No Integration Selected",
              description: "No integration selected message",
            }),
          headerIcon: integrationData ? (
            <ApplicationIcon
              logo={getApplicationLogo(integrationData.integrationType)}
              size="medium"
            />
          ) : (
            <ApplicationIcon logo="Salesforce" size="medium" />
          ),
        }}
        layout={
          integrationData ? (
            <AgentforceIntegrationSidePanelContent
              integration={integrationData}
              credentials={credentials}
              isLoadingCredentials={isLoadingCredentials}
              isCredentialsError={isCredentialsError}
            />
          ) : (
            <div />
          )
        }
        footer={
          integration ? (
            <IntegrationSidePanelFooter
              integration={integration}
              onDeleteClick={() => setIsDeleteModalOpen(true)}
            />
          ) : undefined
        }
        isOpen={isOpen}
        onClose={onClose}
        isSidePanelNavigatingList
        handlePrevious={() => {}}
        handleNext={() => {}}
        isPreviousDisabled={true}
        isNextDisabled={true}
        isMobileWidth={isMobileXlargeWidth}
        isTabletWidth={isTabletMediumWidth}
        shouldConditionallyRender={false}
        className="Integrations-sidePanel"
      />
      <DeleteIntegrationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        integrationId={integration?.id || ""}
        integrationName={integration?.displayName}
        onDeleteSuccess={() => {
          onClose();
        }}
      />
    </>
  );
}
